import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import SitesService from 'src/services/sites/sitesService';
import { Region } from 'src/services/tasks/types';
import UserSiteConfigService from 'src/services/userSiteConfigService';
import { SortStrategy, sortBasedOnStrategy } from 'src/utils/sortStrategies';

// **** Constants **** //

export const NO_SITE = { site: '', id: 0, regionId: 0, countryId: 0 };

const initialState: SiteState = {
  allowedSites: [],
  allSites: [],
  userSites: [],
  selectedSite: NO_SITE,
  isSiteSelectionDisabled: false,
  preferredSitesAndAllowedSitesFetched: false,
  hasAllowedSite: true,
  activeSites: [],
};

// **** Types **** //

export type SiteState = {
  allSites: Site[];
  userSites: Site[];
  allowedSites: Site[];
  // TODO selectedSites is set from an allowedSites item but is not kept up to date with it.
  // This can cause selectedSite feature flag values to become stale. They are currently unused.
  selectedSite: Site;
  isSiteSelectionDisabled: boolean;
  preferredSitesAndAllowedSitesFetched: boolean;
  hasAllowedSite: boolean;
  activeSites: Site[];
};

export interface Site {
  site: string;
  id: number;
  regionId: number;
  countryId: number;
  participatingInDecisionHubPilot?: boolean;
  jobModificationsEnabled?: boolean;
  region?: Partial<Region>;
  siteLocation?: string;
}

export interface SiteLabel {
  label: string;
  value: number;
}

interface UpdatedUserSitesParamType {
  alias: string;
  sites: Site[];
}

interface GetUserSitesParamType {
  alias: string;
}

// **** API calls **** //

/**
 * Fetch all the allowed and preferred sites.
 */
export const getAllowedAndPreferredSites = createAsyncThunk(
  'site/getAllowedAndPreferredSites',
  async (alias: string) => {
    const { data: allowedSites } = await UserSiteConfigService.getAllowedSites();
    let { data: preferredSites } = await UserSiteConfigService.getUserSiteConfig(alias);
    // preferredSites will be "" before the user has saved anything in the default settings section of RTW
    // Note that there is a difference between "saving no preferred site" and "before saving any preferred site"
    preferredSites = preferredSites === '' ? { defaultSites: [] } : preferredSites;
    return { allowedSites, preferredSites };
  },
);

/**
 * Fetch all the allowed sites
 */
export const getAllowedSites = createAsyncThunk('site/getAllowedSites', async () => {
  const { data } = await UserSiteConfigService.getAllowedSites();
  return data;
});

/**
 * Fetch all the sites
 */
export const getSites = createAsyncThunk('site/getAllSites', async () => {
  // TODO this operation is frequently executed, implement etag headers to avoid redundant data fetching
  const { data } = await SitesService.getSites();
  return data;
});

/**
 * Fetch all active sites
 */
export const getAllActiveSites = createAsyncThunk('site/getAllActive', async () => {
  const { data } = await SitesService.getActiveSites();
  return data;
});

/**
 * Fetch this users sites
 */
export const getUserSites = createAsyncThunk('sites/getUserSites', async ({ alias }: GetUserSitesParamType) => {
  const { data } = await UserSiteConfigService.getUserSiteConfig(alias);
  return data == '' ? { defaultSites: [] } : data;
});

/**
 * Update the sites
 */
export const updateUserSites = createAsyncThunk(
  'sites/updateUserSites',
  async ({ alias, sites }: UpdatedUserSitesParamType) => {
    const { data } = await UserSiteConfigService.updateUserSites(alias, sites);
    return data;
  },
);

// **** Setup **** //

const { reducer, actions } = createSlice({
  name: 'sites',
  initialState,
  reducers: {
    setSelectedSite: (state, action) => {
      state.selectedSite = action.payload;
    },
    setIsSiteSelectionDisabled: (state, action) => {
      state.isSiteSelectionDisabled = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSites.fulfilled, (state, { payload }) => {
      state.allSites = payload;
    });
    builder.addCase(getAllowedSites.fulfilled, (state, { payload }) => {
      if (payload.length === 0) {
        state.hasAllowedSite = false;
      }
      state.allowedSites = payload;
    });
    builder.addCase(getUserSites.fulfilled, (state, { payload }) => {
      state.userSites = payload.defaultSites;
    });
    builder.addCase(updateUserSites.fulfilled, (state, { payload }) => {
      state.userSites = payload.defaultSites;
    });
    builder.addCase(getAllowedAndPreferredSites.fulfilled, (state, { payload }) => {
      state.userSites = payload.preferredSites.defaultSites;
      state.allowedSites = payload.allowedSites;
      if (state.allowedSites.length === 0) {
        state.hasAllowedSite = false;
      } else {
        state.preferredSitesAndAllowedSitesFetched = true;
      }
    });
    builder.addCase(getAllowedAndPreferredSites.rejected, (state) => {
      state.hasAllowedSite = false;
    });
    builder.addCase(getAllActiveSites.fulfilled, (state, { payload }) => {
      state.activeSites = sortBasedOnStrategy(SortStrategy.DEFAULT, payload, 'site', 'ascending');
    });
  },
});

// **** Export **** //

export const { setSelectedSite, setIsSiteSelectionDisabled } = actions;
export default reducer;
